import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Lock,
  LogOut,
  Upload,
  Trash2,
  Copy,
  Check,
  Image as ImageIcon,
  Mail,
  Users,
  Loader2,
  Download,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("a4a_admin_token") || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [tab, setTab] = useState("images");

  const [images, setImages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState("");

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const logout = useCallback(() => {
    localStorage.removeItem("a4a_admin_token");
    setToken("");
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [imgRes, cRes, nRes] = await Promise.all([
        axios.get(`${API}/images`),
        axios.get(`${API}/admin/contacts`, authHeader),
        axios.get(`${API}/admin/newsletter`, authHeader),
      ]);
      setImages(imgRes.data);
      setContacts(cRes.data);
      setSubs(nRes.data);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem("a4a_admin_token", res.data.token);
      setToken(res.data.token);
      setPassword("");
    } catch (err) {
      setLoginError("Password errata. Riprova.");
    } finally {
      setLoggingIn(false);
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/admin/images`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      }
      await loadAll();
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else alert("Errore durante il caricamento: " + (err?.response?.data?.detail || "riprova"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = async (id) => {
    if (!window.confirm("Eliminare questa immagine?")) return;
    try {
      await axios.delete(`${API}/admin/images/${id}`, authHeader);
      setImages((imgs) => imgs.filter((i) => i.id !== id));
    } catch (err) {
      if (err?.response?.status === 401) logout();
    }
  };

  const copyUrl = (url) => {
    const full = `${BACKEND_URL}${url}`;
    navigator.clipboard?.writeText(full);
    setCopied(url);
    setTimeout(() => setCopied(""), 1500);
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/newsletter/export`, {
        ...authHeader,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "iscritti_newsletter.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.status === 401) logout();
      else alert("Errore durante l'esportazione. Riprova.");
    }
  };

  // ---------- LOGIN VIEW ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#1c1b1a] flex items-center justify-center px-5">
        <form onSubmit={login} className="w-full max-w-sm bg-[#faf7f2] p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-[#b5533a] flex items-center justify-center">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h1 className="mt-4 font-serif text-3xl text-[#1c1b1a]">Area riservata</h1>
            <p className="text-sm text-[#8a8478] mt-1">arts4all · gestione contenuti</p>
          </div>
          <label className="block text-xs uppercase tracking-[0.16em] text-[#8a8478] mb-2">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="w-full border border-[#dcd4c6] bg-white px-4 py-3 outline-none focus:border-[#b5533a] transition-colors"
          />
          {loginError && <p className="mt-3 text-sm text-[#c0392b]">{loginError}</p>}
          <button
            type="submit"
            disabled={loggingIn}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#b5533a] hover:bg-[#a0472f] disabled:opacity-60 text-white px-6 py-3.5 text-sm uppercase tracking-[0.14em] transition-colors"
          >
            {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Accedi
          </button>
        </form>
      </div>
    );
  }

  // ---------- DASHBOARD VIEW ----------
  const tabs = [
    { id: "images", label: "Immagini", icon: ImageIcon },
    { id: "contacts", label: `Contatti (${contacts.length})`, icon: Mail },
    { id: "newsletter", label: `Newsletter (${subs.length})`, icon: Users },
  ];

  return (
    <div className="min-h-screen bg-[#f2ede4]">
      {/* Topbar */}
      <header className="bg-[#1c1b1a] text-white">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <span className="font-serif text-2xl">
            arts<span className="text-[#e08a5f]">4</span>all
            <span className="ml-3 text-xs uppercase tracking-[0.2em] text-[#a49d90]">Admin</span>
          </span>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-[#d8d2c7] hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-[#e6dfd2]">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 flex gap-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 py-4 text-sm border-b-2 transition-colors ${
                  tab === t.id
                    ? "border-[#b5533a] text-[#b5533a]"
                    : "border-transparent text-[#6a655c] hover:text-[#1c1b1a]"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-5 lg:px-8 py-10">
        {/* IMAGES */}
        {tab === "images" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <div>
                <h2 className="font-serif text-3xl text-[#1c1b1a]">Immagini</h2>
                <p className="text-sm text-[#8a8478] mt-1">
                  Carica le tue foto. Copia l'URL per usarle nel sito.
                </p>
              </div>
              <label className="inline-flex items-center gap-2 bg-[#b5533a] hover:bg-[#a0472f] text-white px-6 py-3 text-sm uppercase tracking-[0.14em] cursor-pointer transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Caricamento..." : "Carica immagini"}
                <input type="file" accept="image/*" multiple hidden onChange={onUpload} disabled={uploading} />
              </label>
            </div>

            {images.length === 0 ? (
              <div className="text-center py-20 text-[#8a8478] border border-dashed border-[#d4ccbc]">
                <ImageIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
                Nessuna immagine caricata.
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {images.map((img) => (
                  <div key={img.id} className="bg-white border border-[#e6dfd2] overflow-hidden">
                    <div className="aspect-[4/3] bg-[#eae4d9] overflow-hidden">
                      <img
                        src={`${BACKEND_URL}${img.url}`}
                        alt={img.filename}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-[#6a655c] truncate" title={img.filename}>
                        {img.filename}
                      </p>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => copyUrl(img.url)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[#dcd4c6] hover:border-[#b5533a] text-[#3d3a35] px-2 py-2 text-xs transition-colors"
                        >
                          {copied === img.url ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-[#6b8e5a]" /> Copiato
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" /> Copia URL
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => removeImage(img.id)}
                          className="inline-flex items-center justify-center border border-[#dcd4c6] hover:border-[#c0392b] hover:text-[#c0392b] text-[#8a8478] px-3 py-2 transition-colors"
                          aria-label="Elimina"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CONTACTS */}
        {tab === "contacts" && (
          <div>
            <h2 className="font-serif text-3xl text-[#1c1b1a] mb-6">Messaggi ricevuti</h2>
            {contacts.length === 0 ? (
              <p className="text-[#8a8478]">Nessun messaggio.</p>
            ) : (
              <div className="space-y-4">
                {contacts.map((c) => (
                  <div key={c.id} className="bg-white border border-[#e6dfd2] p-5">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-medium text-[#1c1b1a]">{c.name}</span>
                      <span className="text-xs text-[#8a8478]">
                        {new Date(c.created_at).toLocaleString("it-IT")}
                      </span>
                    </div>
                    <a href={`mailto:${c.email}`} className="text-sm text-[#b5533a] hover:underline">
                      {c.email}
                    </a>
                    {c.subject && <p className="mt-1 text-sm text-[#6a655c] italic">{c.subject}</p>}
                    <p className="mt-2 text-[#3d3a35] whitespace-pre-line">{c.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* NEWSLETTER */}
        {tab === "newsletter" && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h2 className="font-serif text-3xl text-[#1c1b1a]">Iscritti alla newsletter</h2>
              {subs.length > 0 && (
                <button
                  onClick={exportCsv}
                  data-testid="export-newsletter-csv-button"
                  className="inline-flex items-center gap-2 bg-[#1c1b1a] hover:bg-[#2a2723] text-white px-5 py-3 text-sm uppercase tracking-[0.14em] transition-colors"
                >
                  <Download className="w-4 h-4" /> Esporta CSV
                </button>
              )}
            </div>
            {subs.length === 0 ? (
              <p className="text-[#8a8478]">Nessun iscritto.</p>
            ) : (
              <div className="bg-white border border-[#e6dfd2] divide-y divide-[#eee8dd]">
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-5 py-3">
                    <span className="text-[#3d3a35]">{s.email}</span>
                    <span className="text-xs text-[#8a8478]">
                      Consenso: {s.consent_at
                        ? new Date(s.consent_at).toLocaleDateString("it-IT")
                        : new Date(s.created_at).toLocaleDateString("it-IT")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
